import React, { useMemo } from "react";
import { Alert } from "@mui/material";
import { Chess } from "chess.js";

interface GameStatusBannerProps {
  chess: Chess;
  fen: string;
  isEditMode: boolean;
}

export const GameStatusBanner: React.FC<GameStatusBannerProps> = ({
  chess,
  fen,
  isEditMode,
}) => {
  const gameStatusInfo = useMemo(() => {
    if (isEditMode) {
      return {
        text: "Edit Mode: Drag pieces freely, drop outside board to delete",
        severity: "info" as const,
      };
    }

    if (chess.isCheckmate()) {
      const winner = chess.turn() === "w" ? "Black" : "White";
      return {
        text: `Checkmate! ${winner} wins!`,
        severity: "error" as const,
      };
    }
    if (chess.isDraw()) {
      let reason = "Draw";
      if (chess.isStalemate()) reason = "Draw by Stalemate";
      else if (chess.isThreefoldRepetition()) reason = "Draw by Threefold Repetition";
      else if (chess.isInsufficientMaterial()) reason = "Draw by Insufficient Material";
      return {
        text: reason,
        severity: "warning" as const,
      };
    }
    if (chess.isCheck()) {
      const side = chess.turn() === "w" ? "White" : "Black";
      return {
        text: `Check! ${side} to move`,
        severity: "warning" as const,
      };
    }

    const side = chess.turn() === "w" ? "White" : "Black";
    return {
      text: `${side} to move`,
      severity: "success" as const,
    };
  }, [isEditMode, chess, fen]);

  return (
    <Alert
      severity={gameStatusInfo.severity}
      icon={false}
      sx={{
        mb: 2,
        py: 0.5,
        fontWeight: 600,
        display: "flex",
        justifyContent: "center",
        textAlign: "center",
        bgcolor:
          gameStatusInfo.severity === "error"
            ? "rgba(239, 68, 68, 0.15)"
            : gameStatusInfo.severity === "warning"
            ? "rgba(234, 179, 8, 0.15)"
            : gameStatusInfo.severity === "info"
            ? "rgba(59, 130, 246, 0.15)"
            : "rgba(34, 197, 94, 0.15)",
        color: "white",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {gameStatusInfo.text}
    </Alert>
  );
};

export default GameStatusBanner;
